import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import Product from '../Types/Product';
import {
  AddAllToCartResponse,
  WishListItem,
  WishListProps,
} from './WishList.reducer';
import { post } from '../Services/http';
import { addToDataLayer } from '../Services/Cart.service';
import {
  AddSharedItemsToCartRequest,
  MinimalWishlistItem,
  SharedwishlistAddToCartParams,
} from '../Components/WishList/types/WishlistTypes';
import {
  handleApiResponse,
  mapToAddTGtmItem,
} from '../Components/WishList/services/WishlistService';

interface SharedWishListState {
  id: string;
  total: number;
  items: WishListItem[];
  addToWishListAnimating: boolean;
  errors?: string[];
}

const DEFAULT_STATE: SharedWishListState = {
  id: '',
  total: 0,
  items: [],
  addToWishListAnimating: false,
};

const mapToMinimalWishlistItem = (item: WishListItem) => {
  const wishlistItem: MinimalWishlistItem = {
    id: item.id,
    variantId: item.variantId,
    quantity: item.quantity,
    listId: item.listId,
  };
  return wishlistItem;
};

export const addSharedWishlistItemsToCart = createAsyncThunk<
  AddAllToCartResponse,
  SharedwishlistAddToCartParams
>(
  'wishlist/shared/addAllToCart',
  async (
    { wishlistItems, email, clickSource, abortController },
    thunkAPI
  ) => {
    const request: AddSharedItemsToCartRequest = {
      wishlistItems: wishlistItems.map((item) =>
        mapToMinimalWishlistItem(item)
      ),
    };

    const response = await post(
      '/api/wishlist/shared/addalltocart',
      request,
      abortController
    );
    const data = await handleApiResponse(response);

    try {
      addToDataLayer(
        wishlistItems.map((i) => mapToAddTGtmItem(i)),
        data.orderRows,
        email,
        clickSource
      );
    } catch (err) {
      console.log(err);
    }

    thunkAPI.dispatch(
      receiveSharedWishlistErrors(data.notInStockVariantIds)
    );

    return data;
  }
);

const SharedWishListSlice = createSlice({
  name: 'sharedWishList',
  initialState: DEFAULT_STATE,
  reducers: {
    removeSharedWishListItem: (
      state,
      action: PayloadAction<{ product: Product }>
    ) => {
      state.items = state.items.filter(
        (item) => item.product.id !== action.payload.product.id
      );
    },
    addSharedWishListItem: (
      state,
      action: PayloadAction<{ product: Product }>
    ) => {
      state.items.push({
        id: action.payload.id,
        listId: action.payload.listId,
        variantId: action.payload.variantId,
        sortIndex: action.payload.sortIndex,
        quantity: 0,
        itemTotal: 0,
        product: action.payload.product,
      });
    },
    updateSharedWishListItemQty: (
      state,
      action: PayloadAction<{
        articleNumber: number;
        quantity: number;
        isBusinessCustomer: boolean;
      }>
    ) => {
      var item = state.items.find(
        (item) =>
          item.product.articleNumber === action.payload.articleNumber
      );

      if (item) {
        item.quantity = action.payload.quantity;
        item.itemTotal =
          action.payload.quantity *
          (item.product.price.campaignPrice
            ? action.payload.isBusinessCustomer
              ? item.product.price.campaignPrice.price
              : item.product.price.campaignPrice.priceWithVat
            : action.payload.isBusinessCustomer
            ? item.product.price.price.price
            : item.product.price.price.priceWithVat);
      }
    },
    receiveSharedWishList: (
      state,
      action: PayloadAction<WishListProps>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateSharedWishlistSelectedVariant: (
      state,
      action: PayloadAction<{ articleNumber?: number; variantId?: string }>
    ) => {
      var item = state.items.find(
        (item) =>
          item.product.articleNumber === action.payload.articleNumber
      );

      if (item) item.variantId = action.payload.variantId;
    },
    updateSharedWishlistTotal: (state) => {
      state.total = Math.ceil(
        state.items.reduce((sum, i) => sum + i.itemTotal, 0)
      );
    },
    receiveSharedWishlistErrors: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.errors = action.payload;
    },
  },
});

export const {
  removeSharedWishListItem,
  addSharedWishListItem,
  receiveSharedWishList,
  updateSharedWishlistSelectedVariant,
  updateSharedWishListItemQty,
  updateSharedWishlistTotal,
  receiveSharedWishlistErrors,
} = SharedWishListSlice.actions;

export default SharedWishListSlice.reducer;
